/**
 * Shape geometry.
 *
 * Pure maths: a drag gives two corners, the shape needs a normalised bounding
 * box and, for lines and arrows, endpoints plus an arrow head. Keeping this here
 * means the drag can be unit-tested without a canvas, and the renderer stays a
 * thin drawing layer.
 */

export type ShapeKind = 'line' | 'rect' | 'ellipse' | 'arrow';

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ShapeGeometry {
  kind: ShapeKind;
  /** Drag endpoints, in layer coordinates, unnormalised. */
  from: Point;
  to: Point;
  /** Normalised bounding box, already padded so a stroke is never clipped. */
  bounds: Rect;
  strokeWidth: number;
}

/** Turn two drag corners into a shape. Always returns a drawable shape. */
export function shapeFromDrag(
  kind: ShapeKind,
  from: Point,
  to: Point,
  strokeWidth: number,
): ShapeGeometry {
  const width = Math.max(1, strokeWidth);
  // Half the stroke sticks out either side of the path; without this padding a
  // rectangle drawn against the layer edge loses its outline.
  const pad = width / 2;
  const left = Math.min(from.x, to.x) - pad;
  const top = Math.min(from.y, to.y) - pad;
  const right = Math.max(from.x, to.x) + pad;
  const bottom = Math.max(from.y, to.y) + pad;
  return {
    kind,
    from,
    to,
    strokeWidth: width,
    bounds: {
      x: left,
      y: top,
      width: Math.max(1, right - left),
      height: Math.max(1, bottom - top),
    },
  };
}

/**
 * The two wing tips of an arrow head at `to`, pointing away from `from`.
 *
 * Computed from the direction vector so a degenerate zero-length drag yields a
 * zero-size head instead of NaN, which would poison the whole path.
 */
export function arrowHead(from: Point, to: Point, size: number): [Point, Point] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length < 1e-6) return [to, to];
  const ux = dx / length;
  const uy = dy / length;
  const wing = Math.max(2, size);
  const back = { x: to.x - ux * wing, y: to.y - uy * wing };
  const perpendicular = { x: -uy, y: ux };
  const half = wing * 0.55;
  return [
    { x: back.x + perpendicular.x * half, y: back.y + perpendicular.y * half },
    { x: back.x - perpendicular.x * half, y: back.y - perpendicular.y * half },
  ];
}

/** True when a drag is too small to be a deliberate shape. */
export function isNegligibleDrag(from: Point, to: Point, threshold = 2): boolean {
  return Math.abs(to.x - from.x) < threshold && Math.abs(to.y - from.y) < threshold;
}
